import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Pie, Line } from 'react-chartjs-2';
import { motion } from 'framer-motion';
import {
    Chart as ChartJS,
    ArcElement,
    LineElement,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip,
    Legend,
} from 'chart.js';
import {
    AlertTriangle,
    BadgeIndianRupee,
    LineChart,
    MessageSquare,
    Smile,
    Star,
    TrendingDown,
    TrendingUp,
    UsersRound,
} from 'lucide-react';
import ReviewsHolder from '../../Components/Feedback/ReviewsHolder';
import { BASE_URL_API } from '../../References/URLs';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import AuthContext from '../../store/auth-context';
import styles from './FeedbackHome.module.css';

// Register required elements for Chart.js
ChartJS.register(
    ArcElement,
    LineElement,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip,
    Legend
);

const FeedbackHome = () => {
    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => {
        try {
            return asCtx?.activeStore && asCtx.activeStore.trim() !== "" ? JSON.parse(asCtx.activeStore) : null;
        } catch {
            return null;
        }
    })();

    const [isLoading, setIsLoading] = useState(true);
    const [feedbackData, setFeedbackData] = useState([]);

    const getAllFeedbacks = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(BASE_URL_API + "getAllFeedbacks", {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                }),
            });
            if (!response.ok) throw new Error("Failed to fetch feedback data");

            const data = await response.json();
            if (data.status === 'success') {
                setFeedbackData(Array.isArray(data.response) ? data.response : []);
            } else if (data.message === 'tokenExpired') {
                authCtx.logout();
            } else {
                setFeedbackData([]);
            }
        } catch (error) {
            console.error("Error fetching feedback data:", error);
            setFeedbackData([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (activeStore !== null) {
            getAllFeedbacks();
        }
    }, [activeStore?.storeIdentifier]);

    const parsedReviews = useMemo(() => {
        return feedbackData
            .map(item => {
                const star = parseInt(item?.selectedStar, 10);
                return {
                    ...item,
                    star: Number.isFinite(star) ? Math.min(5, Math.max(1, star)) : 0,
                    ratedDate: item?.ratedOn ? new Date(item.ratedOn.replace(' ', 'T')) : null,
                    amount: parseFloat(item?.amountToBeCollected || '0') || 0,
                    note: (item?.careToShareALilMoreTxt || '').trim(),
                    serviceScore: parseInt(item?.starSelectedServiceResults || '0', 10) || 0,
                    staffScore: parseInt(item?.starSelectedStaffBehaviour || '0', 10) || 0,
                    ambianceScore: parseInt(item?.starSelectedSalonEnvironment || '0', 10) || 0,
                };
            })
            .filter(item => item.star > 0);
    }, [feedbackData]);

    const distribution = useMemo(() => {
        const d = [0, 0, 0, 0, 0];
        parsedReviews.forEach(item => {
            d[item.star - 1] += 1;
        });
        return d;
    }, [parsedReviews]);

    const trend = useMemo(() => {
        const byDate = parsedReviews.reduce((acc, item) => {
            if (!item.ratedDate || Number.isNaN(item.ratedDate.getTime())) return acc;
            const key = item.ratedDate.toISOString().slice(0, 10);
            if (!acc[key]) acc[key] = { total: 0, count: 0 };
            acc[key].total += item.star;
            acc[key].count += 1;
            return acc;
        }, {});

        const entries = Object.entries(byDate)
            .sort(([a], [b]) => new Date(a) - new Date(b))
            .slice(-14);

        return {
            labels: entries.map(([date]) => new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })),
            values: entries.map(([, info]) => parseFloat((info.total / info.count).toFixed(2))),
            volumes: entries.map(([, info]) => info.count),
        };
    }, [parsedReviews]);

    const insights = useMemo(() => {
        const total = parsedReviews.length;
        const fiveStar = distribution[4] || 0;
        const fourStar = distribution[3] || 0;
        const lowStar = (distribution[0] || 0) + (distribution[1] || 0);
        const neutral = distribution[2] || 0;
        const positive = fourStar + fiveStar;
        const avgRating = total > 0
            ? parseFloat((parsedReviews.reduce((s, i) => s + i.star, 0) / total).toFixed(2))
            : 0;

        const textFeedbackCount = parsedReviews.filter(item => item.note.length > 0).length;
        const responseCoverage = total > 0 ? Math.round((textFeedbackCount / total) * 100) : 0;

        const detailedRows = parsedReviews.filter(item => item.serviceScore > 0 || item.staffScore > 0 || item.ambianceScore > 0);
        const avgDetailed = detailedRows.length > 0
            ? {
                service: (detailedRows.reduce((s, i) => s + (i.serviceScore || 0), 0) / detailedRows.length).toFixed(1),
                staff: (detailedRows.reduce((s, i) => s + (i.staffScore || 0), 0) / detailedRows.length).toFixed(1),
                ambiance: (detailedRows.reduce((s, i) => s + (i.ambianceScore || 0), 0) / detailedRows.length).toFixed(1),
            }
            : { service: '0.0', staff: '0.0', ambiance: '0.0' };

        const keywordRules = {
            haircut: ['hair', 'haircut', 'trim'],
            service: ['service', 'result', 'finish', 'quality'],
            staff: ['staff', 'behaviour', 'behavior', 'rude', 'friendly'],
            wait: ['wait', 'delay', 'late', 'time'],
            hygiene: ['clean', 'hygiene', 'dirty', 'environment'],
            pricing: ['price', 'cost', 'expensive', 'amount'],
        };
        const keywordCount = Object.keys(keywordRules).reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
        parsedReviews.forEach(item => {
            const text = item.note.toLowerCase();
            if (!text) return;
            Object.entries(keywordRules).forEach(([bucket, words]) => {
                if (words.some(word => text.includes(word))) {
                    keywordCount[bucket] += 1;
                }
            });
        });
        const topConcern = Object.entries(keywordCount).sort((a, b) => b[1] - a[1])[0] || ['none', 0];

        const averageTicket = total > 0
            ? Math.round(parsedReviews.reduce((sum, item) => sum + item.amount, 0) / total)
            : 0;

        const last7 = trend.values.slice(-7);
        const prev7 = trend.values.slice(-14, -7);
        const nowAvg = last7.length > 0 ? last7.reduce((a, b) => a + b, 0) / last7.length : 0;
        const prevAvg = prev7.length > 0 ? prev7.reduce((a, b) => a + b, 0) / prev7.length : 0;
        const momentum = nowAvg - prevAvg;

        return {
            total,
            avgRating,
            positive,
            lowStar,
            neutral,
            fiveStar,
            responseCoverage,
            textFeedbackCount,
            avgDetailed,
            topConcern,
            averageTicket,
            momentum,
        };
    }, [parsedReviews, distribution, trend]);

    const pieData = {
        labels: ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'],
        datasets: [{
            label: 'Rating Distribution',
            data: distribution,
            backgroundColor: ['#ef4444', '#f97316', '#facc15', '#22c55e', '#6366f1'],
            borderWidth: 0,
            hoverOffset: 3,
        }]
    };

    const lineData = {
        labels: trend.labels,
        datasets: [{
            label: 'Avg Rating',
            data: trend.values,
            fill: true,
            borderColor: '#4f46e5',
            backgroundColor: 'rgba(79, 70, 229, 0.10)',
            pointBackgroundColor: '#4f46e5',
            pointRadius: 2,
            pointHoverRadius: 3,
            tension: 0.35,
            borderWidth: 2,
        }]
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    boxHeight: 6,
                    boxWidth: 8,
                    color: '#475569',
                    font: { size: 11, weight: '600' },
                    padding: 12,
                },
            },
            tooltip: {
                backgroundColor: '#0f172a',
                titleColor: '#f8fafc',
                bodyColor: '#e2e8f0',
                padding: 8,
                displayColors: false,
            },
        },
    };

    const lineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: '#0f172a',
                titleColor: '#f8fafc',
                bodyColor: '#e2e8f0',
                padding: 8,
                displayColors: false,
            },
        },
        scales: {
            x: {
                ticks: { color: '#64748b', font: { size: 10 } },
                grid: { display: false },
            },
            y: {
                min: 0,
                max: 5,
                ticks: { color: '#64748b', font: { size: 10 }, stepSize: 1 },
                grid: { color: '#e2e8f0' },
            },
        },
    };

    if (!activeStore) {
        return <div className={styles.errorMessage}>Please select a store first!</div>;
    }

    return (
        <motion.div className={styles.mainWrapper} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35 }}>
            <div className={styles.pageHeader}>
                <div className={styles.pageHeaderLeft}>
                    <span className={styles.statusPill}>Experience Intelligence</span>
                    <h1 className={styles.pageTitle}>Feedback Insights</h1>
                    <p className={styles.pageSubtitle}>
                        Minimal, high-signal review analytics for quality, sentiment, and service momentum.
                    </p>
                </div>
                <div className={styles.storeMeta}>{activeStore?.storeName || 'Selected Store'}</div>
            </div>

            <div className={styles.kpiGrid}>
                <div className={`${styles.kpiCard} ${styles.kpiCardAvg}`}>
                    <span className={styles.kpiLabel}><Star size={13} /> Average Rating</span>
                    <span className={styles.kpiValue}>{insights.avgRating.toFixed(2)}</span>
                    <span className={styles.kpiMeta}>{insights.total} total reviews</span>
                </div>

                <div className={`${styles.kpiCard} ${styles.kpiCardPositive}`}>
                    <span className={styles.kpiLabel}><Smile size={13} /> Positive Mix</span>
                    <span className={styles.kpiValue}>{insights.positive}</span>
                    <span className={styles.kpiMeta}>
                        <span className={styles.metaStars}>
                            <Star size={11} fill="currentColor" />
                            <span>4</span>
                        </span>
                        <span className={styles.metaAnd}>and</span>
                        <span className={styles.metaStars}>
                            <Star size={11} fill="currentColor" />
                            <span>5</span>
                        </span>
                        <span>reviews</span>
                    </span>
                </div>

                <div className={`${styles.kpiCard} ${styles.kpiCardRisk}`}>
                    <span className={styles.kpiLabel}><AlertTriangle size={13} /> Risk Reviews</span>
                    <span className={styles.kpiValue}>{insights.lowStar}</span>
                    <span className={styles.kpiMeta}>
                        <span className={styles.metaStars}>
                            <Star size={11} fill="currentColor" />
                            <span>1</span>
                        </span>
                        <span className={styles.metaAnd}>and</span>
                        <span className={styles.metaStars}>
                            <Star size={11} fill="currentColor" />
                            <span>2</span>
                        </span>
                        <span>reviews</span>
                    </span>
                </div>

                <div className={`${styles.kpiCard} ${styles.kpiCardCoverage}`}>
                    <span className={styles.kpiLabel}><MessageSquare size={13} /> Text Coverage</span>
                    <span className={styles.kpiValue}>{insights.responseCoverage}%</span>
                    <span className={styles.kpiMeta}>{insights.textFeedbackCount} with comments</span>
                </div>
            </div>

            <div className={styles.bodyWrapper}>
                <div className={styles.leftWrapper}>
                    <div className={styles.chartCard}>
                        <div className={styles.cardHead}>
                            <h3>Rating Distribution</h3>
                            <span className={styles.miniNote}>5-level spread</span>
                        </div>
                        <div className={styles.pieZone}>
                            <Pie data={pieData} options={pieOptions} />
                        </div>
                    </div>

                    <div className={styles.chartCard}>
                        <div className={styles.cardHead}>
                            <h3>Trend (Last 14 Active Days)</h3>
                            <span className={`${styles.trendTag} ${insights.momentum >= 0 ? styles.trendUp : styles.trendDown}`}>
                                {insights.momentum >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                {insights.momentum >= 0 ? '+' : ''}{insights.momentum.toFixed(2)}
                            </span>
                        </div>
                        <div className={styles.lineZone}>
                            <Line height={112} data={lineData} options={lineOptions} />
                        </div>
                    </div>

                    <div className={styles.insightCard}>
                        <div className={styles.cardHead}>
                            <h3>Smart Analyzer</h3>
                            <LineChart size={14} className={styles.cardIcon} />
                        </div>

                        <div className={styles.insightGrid}>
                            <div className={styles.insightItem}>
                                <span className={styles.insightLabel}><UsersRound size={12} /> Neutral Reviews</span>
                                <span className={styles.insightValue}>{insights.neutral}</span>
                            </div>
                            <div className={styles.insightItem}>
                                <span className={styles.insightLabel}><BadgeIndianRupee size={12} /> Avg Ticket</span>
                                <span className={styles.insightValue}>₹{insights.averageTicket}</span>
                            </div>
                            <div className={styles.insightItem}>
                                <span className={styles.insightLabel}>Top Concern Signal</span>
                                <span className={styles.insightValueText}>
                                    {insights.topConcern[1] > 0
                                        ? `${insights.topConcern[0]} (${insights.topConcern[1]})`
                                        : 'No dominant concern'}
                                </span>
                            </div>
                        </div>

                        <div className={styles.detailScores}>
                            <span>Service: <strong>{insights.avgDetailed.service}</strong></span>
                            <span>Staff: <strong>{insights.avgDetailed.staff}</strong></span>
                            <span>Ambiance: <strong>{insights.avgDetailed.ambiance}</strong></span>
                        </div>
                    </div>
                </div>

                <div className={styles.rightWrapper}>
                    <ReviewsHolder isLoading={isLoading} feedbackData={feedbackData} />
                </div>
            </div>
        </motion.div>
    );
};

export default FeedbackHome;
